import React, { useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DownloadIcon from '@mui/icons-material/Download';

const PreviewModal = ({
  open,
  onClose,
  onBack,
  mockTradeData,
  parsedData,
  strategiesData = []
}) => {

  // Determine which data to use
  const dataToUse = parsedData?.data || mockTradeData || [];
  

  // Function to find account ID for a given strategy ID
  const getAccountIdForStrategy = (strategyId) => {
   
    // Look for user strategy where strategy.id matches the order's strategy ID
    const userStrategy = strategiesData.find(s => s.strategy?.id === strategyId);
   
    const accountId = userStrategy?.accountId || null;
    
    return accountId;
  };

  // Transform API data to CSV format for buy/sell orders
  const transformToCSVFormat = (orders) => {
    const csvRecords = [];
    

    // Check if this is raw order data or already transformed data
    const isRawOrderData = orders && orders.length > 0 && orders[0].transactions;
   
    
    if (!isRawOrderData) {
  
      // Create sample data based on your exact structure for testing
      const sampleOrder = {
       
      };
      
      // Process the sample order
      orders = [sampleOrder];
     
    }
    
    orders?.forEach((order, orderIndex) => {
      
      
      // Skip orders with type MANAGER_DISCRETION
      if (order.type === 'MANAGER_DISCRETION') {
       
        return;
      }

      // Check if this is a selected record (has _selectedRecordIndex and _transactionIndex)
      const isSelectedRecord = order._selectedRecordIndex !== undefined && order._transactionIndex !== undefined;
     
      
      // Use full names instead of abbreviated codes
      const actionMap = {
        'BUY': 'BUY',
        'SELL': 'SELL',
        'EXCHANGE': 'EXCHANGE',
        'SWAP': 'SWAP'
      };

      // Use full security type names
      const securityTypeMap = {
        'STK': 'STOCK', // Equity
        'MUTUAL_FUND': 'MUTUAL_FUND', // Mutual Fund
        'ETF': 'ETF' // ETF
      };

      // Use full order type names
      const orderTypeMap = {
        'MKT': 'MARKET', // Market Order
        'LMT': 'LIMIT', // Limit Order
        'STP': 'STOP', // Stop Order
        'STL': 'STOP_LIMIT', // Stop Limit Order
        'TSP': 'TRAILING_STOP_PERCENT', // Trailing Stop Percent
        'TSD': 'TRAILING_STOP_DOLLAR' // Trailing Stop Dollar
      };

      // Get security type from strategy
      const securityType = securityTypeMap[order.strategy?.securityType] || order.strategy?.securityType || 'STOCK';
      
      // Get order type
      const orderType = orderTypeMap[order.type] || order.type || 'MARKET';
      
      // Check if we should show limit price column
      const showLimitPrice = order.type === 'LMT';
      
      // Time in force
      const timeInForce = order.timeInForce || 'DAY';
      
      // Reinvest options (default to Y for mutual funds)
      const reinvestDividends = securityType === 'M' ? 'Y' : '';
      const transactionFee = securityType === 'M' ? 'Y' : '';
      const reinvestCapitalGains = securityType === 'M' ? 'Y' : '';

      // Create one record per transaction
      if (order.transactions && order.transactions.length > 0) {
       
        order.transactions.forEach((transaction, transactionIndex) => {
          // If this is a selected record, only process the specific transaction
          if (isSelectedRecord && transactionIndex !== order._transactionIndex) {
           
            return;
          }
          
        
          
          const record = {
            'Account Number': getAccountIdForStrategy(order.strategy?.id) || null, // Get account ID from strategies data
            'Account Type': '', // Leave blank as per requirements
            'Security Type': securityType,
            'Action': actionMap[order.action] || order.action || 'BUY',
            'Quantity Type': null, // Set to null as requested
            'Quantity': transaction.quantity || '',
            'Symbol': transaction.asset?.symbol || 'N/A',
            'Exchange/Swap to Symbol': '', // Leave blank for now
            'Order Type': orderType,
            'Time in Force': timeInForce,
            'Do Not Reduce': '', // Leave blank as per requirements
            'All or None': '', // Leave blank as per requirements
            'Reinvest Dividends': reinvestDividends,
            'Transaction Fee': transactionFee,
            'Reinvest Capital Gains': reinvestCapitalGains
          };

          // Add limit price only if order type is LMT
          if (showLimitPrice) {
            record['Limit Price'] = transaction.limitPrice || '';
          } else {
            record['Limit Price'] = '';
          }

          // Add stop price (empty for now)
          record['Stop Price'] = '';

         
          csvRecords.push(record);
        });
      } else {
        // If no transactions, create a single record with order data
        // Only process if this is not a selected record or if it's the selected transaction (index 0)
        if (isSelectedRecord && order._transactionIndex !== 0) {
        
          return;
        }
        
        const record = {
          'Account Number': getAccountIdForStrategy(order.strategy?.id) || null, // Get account ID from strategies data
          'Account Type': '',
          'Security Type': securityType,
          'Action': actionMap[order.action] || order.action || 'BUY',
          'Quantity Type': null,
          'Quantity': order.amount || '',
          'Symbol': 'N/A',
          'Exchange/Swap to Symbol': '',
          'Order Type': orderType,
          'Time in Force': timeInForce,
          'Do Not Reduce': '',
          'All or None': '',
          'Reinvest Dividends': reinvestDividends,
          'Transaction Fee': transactionFee,
          'Reinvest Capital Gains': reinvestCapitalGains
        };

        // Add limit price only if order type is LMT
        if (showLimitPrice) {
          record['Limit Price'] = order.limitPrice || '';
        } else {
          record['Limit Price'] = '';
        }

        // Add stop price (empty for now)
        record['Stop Price'] = '';

        csvRecords.push(record);
      }
    });
    return csvRecords;
  };

  // Get CSV data
  const csvData = transformToCSVFormat(dataToUse);
  

  // Check if any order has LMT type to show Limit Price column
  const hasLimitOrders = dataToUse?.some(order => order.type === 'LMT') || false;

  // Function to download CSV
  const downloadCSV = () => {
    if (!csvData || csvData.length === 0) return;

    // Get filtered headers (exclude Limit Price if no LMT orders)
    const filteredHeaders = Object.keys(csvData[0] || {})
      .filter(header => hasLimitOrders || header !== 'Limit Price');

    // Create CSV content
    const csvContent = [
      // Header row
      filteredHeaders.join(','),
      // Data rows
      ...csvData.map(row => 
        filteredHeaders.map(header => `"${row[header] || ''}"`).join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `trades_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="preview-downloading-trades-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <Box sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1200px', // Increased width for better table display
        height: '90vh', // Fixed height instead of maxHeight
        overflow: 'hidden', // Remove overflow from main container
        position: 'relative',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 0 24px',
          flexShrink: 0 // Prevent header from shrinking
        }}>
           <Typography variant="h4" sx={{
             color: '#141218',
             fontFamily: 'Founders Grotesk, sans-serif',
             fontSize: '24px',
             fontWeight: '600',
             margin: '0'
           }}>
             Preview Downloading Trades (CSV Format)
           </Typography>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <CloseIcon sx={{ fontSize: 24, color: '#666' }} />
          </IconButton>
        </Box>

        {/* Trade Data Table */}
        <Box sx={{ 
          padding: '24px',
          flex: 1, // Take up remaining space
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Record Count */}
          <Box sx={{ marginBottom: '8px', flexShrink: 0 }}>
            <Typography sx={{
              color: '#666',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: '400'
            }}>
              Showing {csvData.length} records
            </Typography>
          </Box>

          {/* Scroll indicator */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '8px',
            opacity: 0.6,
            flexShrink: 0
          }}>
            <Typography sx={{
              fontSize: '12px',
              color: '#666',
              fontStyle: 'italic'
            }}>
              ← Scroll horizontally to view all columns →
            </Typography>
          </Box>
           <Box sx={{
             border: '1px solid #E6E6E6',
             borderRadius: '8px',
             overflow: 'hidden',
             flex: 1, // Take up remaining space in the container
             overflowY: 'auto',
             overflowX: 'auto',
             position: 'relative',
             '&::-webkit-scrollbar': {
               height: '8px',
               width: '8px'
             },
             '&::-webkit-scrollbar-track': {
               background: '#f1f1f1',
               borderRadius: '4px'
             },
             '&::-webkit-scrollbar-thumb': {
               background: '#c1c1c1',
               borderRadius: '4px',
               '&:hover': {
                 background: '#a8a8a8'
               }
             },
             // Ensure smooth scrolling
             scrollBehavior: 'smooth'
           }}>
             {/* Table Header */}
             <Box sx={{
               display: 'flex',
               backgroundColor: '#F8F9FA',
               borderBottom: '1px solid #E6E6E6',
               position: 'sticky',
               top: 0,
               zIndex: 2,
               minWidth: '2500px', // Increased width for CSV format columns with full names
               width: '100%',
               boxShadow: '0 2px 4px rgba(0,0,0,0.1)' // Add shadow to make sticky header more visible
             }}>
               {csvData.length > 0 ? (
                 // CSV Format Headers - filter out Limit Price if no LMT orders
                 Object.keys(csvData[0] || {})
                   .filter(header => hasLimitOrders || header !== 'Limit Price')
                   .map((header, index) => (
                   <Box key={index} sx={{
                     flex: header === 'Account Number' || header === 'Symbol' ? '1.2' : 
                           header === 'Exchange/Swap to Symbol' ? '1.5' : '1',
                     padding: '12px 16px',
                     borderRight: index < Object.keys(csvData[0] || {}).filter(h => hasLimitOrders || h !== 'Limit Price').length - 1 ? '1px solid #E6E6E6' : 'none',
                     minWidth: header === 'Account Number' ? '150px' : 
                              header === 'Account Type' ? '120px' :
                              header === 'Security Type' ? '180px' : // Increased for full names
                              header === 'Action' ? '120px' : // Increased for full names
                              header === 'Quantity Type' ? '120px' :
                              header === 'Quantity' ? '120px' :
                              header === 'Symbol' ? '150px' :
                              header === 'Exchange/Swap to Symbol' ? '180px' :
                              header === 'Order Type' ? '200px' : // Increased for full names
                              header === 'Limit Price' ? '120px' :
                              header === 'Stop Price' ? '120px' :
                              header === 'Time in Force' ? '150px' :
                              header === 'Do Not Reduce' ? '150px' :
                              header === 'All or None' ? '120px' :
                              header === 'Reinvest Dividends' ? '180px' :
                              header === 'Transaction Fee' ? '150px' :
                              header === 'Reinvest Capital Gains' ? '200px' : '120px',
                     display: 'flex',
                     alignItems: 'center',
                     backgroundColor: '#F8F9FA'
                   }}>
                     <Typography sx={{
                       color: '#141218',
                       fontFamily: 'Roboto, sans-serif',
                       fontSize: '12px',
                       fontWeight: '600',
                       whiteSpace: 'nowrap',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis'
                     }}>
                       {header}
                     </Typography>
                   </Box>
                 ))
               ) : (
                // Fallback headers for mockTradeData
                <>
                  <Box sx={{ 
                    flex: '2', 
                    padding: '12px 20px',
                    backgroundColor: '#F8F9FA',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Typography sx={{ color: '#141218', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: '600' }}>
                      Investor Email
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    flex: '1', 
                    padding: '12px 20px',
                    backgroundColor: '#F8F9FA',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Typography sx={{ color: '#141218', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: '600' }}>
                      Strategy
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    flex: '1', 
                    padding: '12px 20px',
                    backgroundColor: '#F8F9FA',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Typography sx={{ color: '#141218', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: '600' }}>
                      Status
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    flex: '1.5', 
                    padding: '12px 20px',
                    backgroundColor: '#F8F9FA',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Typography sx={{ color: '#141218', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: '600' }}>
                      Registered
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    flex: '1.5', 
                    padding: '12px 20px',
                    backgroundColor: '#F8F9FA',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Typography sx={{ color: '#141218', fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: '600' }}>
                      Amount ($)
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

             {/* Table Rows */}
            
             {csvData && csvData.length > 0 ? (
               csvData.map((row, rowIndex) => (
                 <Box key={rowIndex} sx={{
                   display: 'flex',
                   borderBottom: rowIndex < csvData.length - 1 ? '1px solid #E6E6E6' : 'none',
                   minWidth: '2500px', // Increased width for CSV format columns with full names
                   '&:hover': {
                     backgroundColor: '#F8F9FA'
                   }
                 }}>
                   {Object.keys(row)
                     .filter(header => hasLimitOrders || header !== 'Limit Price')
                     .map((header, colIndex) => (
                     <Box key={colIndex} sx={{
                       flex: header === 'Account Number' || header === 'Symbol' ? '1.2' : 
                             header === 'Exchange/Swap to Symbol' ? '1.5' : '1',
                       padding: '8px 16px',
                       borderRight: colIndex < Object.keys(row).filter(h => hasLimitOrders || h !== 'Limit Price').length - 1 ? '1px solid #E6E6E6' : 'none',
                       minWidth: header === 'Account Number' ? '150px' : 
                                header === 'Account Type' ? '120px' :
                                header === 'Security Type' ? '180px' : // Increased for full names
                                header === 'Action' ? '120px' : // Increased for full names
                                header === 'Quantity Type' ? '120px' :
                                header === 'Quantity' ? '120px' :
                                header === 'Symbol' ? '150px' :
                                header === 'Exchange/Swap to Symbol' ? '180px' :
                                header === 'Order Type' ? '200px' : // Increased for full names
                                header === 'Limit Price' ? '120px' :
                                header === 'Stop Price' ? '120px' :
                                header === 'Time in Force' ? '150px' :
                                header === 'Do Not Reduce' ? '150px' :
                                header === 'All or None' ? '120px' :
                                header === 'Reinvest Dividends' ? '180px' :
                                header === 'Transaction Fee' ? '150px' :
                                header === 'Reinvest Capital Gains' ? '200px' : '120px',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'flex-start'
                     }}>
                       <Typography sx={{
                         color: '#141218',
                         fontFamily: 'Roboto, sans-serif',
                         fontSize: '12px',
                         fontWeight: '400',
                         whiteSpace: 'nowrap',
                         overflow: 'hidden',
                         textOverflow: 'ellipsis',
                         width: '100%'
                       }}>
                         {row[header] || 'N/A'}
                       </Typography>
                     </Box>
                   ))}
                 </Box>
               ))
             ) : (
               // Fallback for when no CSV data is available
               <Box sx={{
                 display: 'flex',
                 alignItems: 'center',
                 justifyContent: 'center',
                 padding: '40px',
                 color: '#666',
                 fontSize: '14px'
               }}>
                 No data available for CSV export
               </Box>
             )}
           </Box>
           
           {/* Scroll Indicators */}
           <Box sx={{
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
             marginTop: '8px',
             color: '#666',
             fontSize: '12px',
             fontStyle: 'italic',
             flexShrink: 0
           }}>
             <Box sx={{
               display: 'flex',
               alignItems: 'center',
               gap: '4px'
             }}>
               <span>← Scroll horizontally to view all columns →</span>
             </Box>
             <Box sx={{
               display: 'flex',
               alignItems: 'center',
               gap: '4px'
             }}>
               <span>↑ Scroll vertically to view all records ↑</span>
             </Box>
           </Box>
         </Box>

        {/* Pagination Controls */}
        {/* <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
          borderTop: '1px solid #E6E6E6',
          backgroundColor: '#FAFAFA',
          flexShrink: 0 // Prevent pagination from shrinking
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Typography sx={{
              color: '#666',
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              fontWeight: '400'
            }}>
              Rows per page:
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              border: '1px solid #E6E6E6',
              borderRadius: '4px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}>
              <Typography sx={{
                color: '#141218',
                fontFamily: 'Roboto, sans-serif',
                fontSize: '14px',
                fontWeight: '400'
              }}>
                5
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#666' }} />
            </Box>
          </Box>
          
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <IconButton size="small" sx={{ p: 1, color: '#999' }}>
              <KeyboardArrowUpIcon sx={{ transform: 'rotate(90deg)', fontSize: 16 }} />
            </IconButton>
            <Box sx={{
              width: '32px',
              height: '32px',
              backgroundColor: '#1344EB',
              color: 'white',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              1
            </Box>
            <IconButton size="small" sx={{ p: 1, color: '#999' }}>
              <KeyboardArrowDownIcon sx={{ transform: 'rotate(90deg)', fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box> */}

        {/* Action Buttons */}
        <Box sx={{
          padding: '24px',
          borderTop: '1px solid #E6E6E6',
          backgroundColor: '#FAFAFA',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0 // Prevent action buttons from shrinking
        }}>
          <Button
            onClick={() => {
           
              
              try {
                if (onBack && typeof onBack === 'function') {
                 
                  onBack();
                 
                } else {
                  console.error('onBack function is not provided or is not a function');
                  // Fallback: close the modal if no onBack function
                  if (onClose && typeof onClose === 'function') {
                   
                    onClose();
                  }
                }
              } catch (error) {
                console.error('Error calling onBack function:', error);
                // Fallback: close the modal on error
                if (onClose && typeof onClose === 'function') {
                 
                  onClose();
                }
              }
            }}
            sx={{
              color: '#1344EB',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '16px',
              padding: '0',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }}
          >
            Back
          </Button>
           <Button
             variant="contained"
             startIcon={<DownloadIcon />}
             onClick={downloadCSV}
             disabled={!csvData || csvData.length === 0}
             sx={{
               backgroundColor: csvData && csvData.length > 0 ? '#1344EB' : '#ccc',
               color: 'white',
               textTransform: 'none',
               fontWeight: '500',
               padding: '12px 24px',
               borderRadius: '6px',
               '&:hover': {
                 backgroundColor: csvData && csvData.length > 0 ? '#0f3ad1' : '#ccc'
               }
             }}
           >
             Download Trades ({csvData?.length || 0})
           </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PreviewModal; 